// `/letter`'S PARTS, AS THE APP DRAWS THEM (WEB-PARITY-6): the phase headers, the grouped sections, the letter paper
// (`InlineLetterPreview`), and the three sheets — "Sign your appeal" (`SignatureEditor`), "Request a change"
// (`LetterFeedbackSheet`) and "Your appeal letter" (`LetterPreviewSheet`, with `EmailCopyRow` under its button).
//
// Chrome only. Every sentence is the app's (`./reviewWording.ts`) or the server's; nothing here decides anything a server
// reads. The one thing made here is the signature's picture, which the server checks again before it draws it.

import { useEffect, useId, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from "react";
import type { EmailLetterResponse, LetterReview, Money, V2Failure, V2Result } from "@contract";
import { money } from "../../format";
import { CopyLine, HeldLine, WithBold } from "../../components/copy";
import { heldText } from "../../components/heldCopy";
import { holdSignature, forgetSignature, useSignature } from "../../app/signature";
import { SIGNATURE_FONT, drawnSignaturePng, typedSignaturePng, type Stroke } from "../../app/signaturePng";
import { word, wordWith, type ReviewWordingId } from "./reviewWording";

/** One of the app's sentences, stamped with its id (`data-held-copy`), as every word this client holds is. */
export function Say({
  id,
  as: Tag = "span",
  className,
  values,
  bold = false,
}: {
  id: ReviewWordingId;
  as?: "h1" | "h2" | "h3" | "p" | "span" | "b" | "small" | "div";
  className?: string;
  values?: Readonly<Record<string, string | number>>;
  /** Render the app's `**bold**` as bold, as SwiftUI's `Text` renders Markdown. */
  bold?: boolean;
}) {
  const text = values === undefined ? word(id) : wordWith(id, values);
  return (
    <Tag className={className} data-held-copy={id}>
      {bold ? <WithBold text={text} /> : text}
    </Tag>
  );
}

/** The app's numbered phase divider (`phaseHeader`, ReviewView.swift:467-495): a disc, the title in capitals, a line. */
export function PhaseHeader({ n, titleId, subtitleId }: { n: number; titleId: ReviewWordingId; subtitleId: ReviewWordingId }) {
  return (
    <div className="rv-phase" data-phase={n}>
      <span className="rv-phase-n" aria-hidden="true">{n}</span>
      <div>
        <Say id={titleId} as="h2" className="rv-phase-title" />
        <Say id={subtitleId} as="p" className="rv-phase-sub" />
      </div>
    </div>
  );
}

/** One of the app's `Form` sections: a header, the rows, a footer. */
export function ReviewSection({
  header,
  footer,
  children,
  ...marks
}: { header?: ReactNode; footer?: ReactNode; children: ReactNode } & Record<`data-${string}`, string | number | undefined>) {
  const headingId = useId();
  return (
    <section className="rv-section" aria-labelledby={header === undefined ? undefined : headingId} {...marks}>
      {header !== undefined && <h3 id={headingId} className="rv-section-header">{header}</h3>}
      <div className="rv-section-body">{children}</div>
      {footer !== undefined && <div className="rv-section-footer">{footer}</div>}
    </section>
  );
}

/** A row the owner presses: an icon tile, a title, a subtitle, a chevron (`setupRow`, ReviewView.swift:590-613). */
export function SetupRow({
  titleId, subtitleId, done = false, onPress,
}: { titleId: ReviewWordingId; subtitleId: ReviewWordingId; done?: boolean; onPress: () => void }) {
  return (
    <button type="button" className={done ? "rv-row rv-row-done" : "rv-row"} onClick={onPress}>
      <span className="rv-row-tile" aria-hidden="true">{done ? "✓" : "✎"}</span>
      <span className="rv-row-text">
        <Say id={titleId} as="b" />
        <Say id={subtitleId} as="small" />
      </span>
      <span className="rv-row-chevron" aria-hidden="true">›</span>
    </button>
  );
}

/** The app's white letter paper (InlineLetterPreview.swift): the letterhead, the filing's own Re: line, faint bars for
 *  the body, the chips, the signature name and the caption, and the button that opens the true PDF. */
export function LetterPaper({
  paper, signatureName, signedCaption, onOpen, busy,
}: {
  paper: LetterReview["paper"];
  signatureName: string;
  /** Null when no signature is held: the paper says "Not signed yet" instead (WALK-FIX-1; draft C, approved 2026-10-05). */
  signedCaption: string | null;
  onOpen: () => void;
  busy: boolean;
}) {
  return (
    <div className="rv-paper">
      <div className="rv-paper-head">
        <span className="rv-paper-brand">
          <Say id="preview.brand.pro" className="rv-paper-pro" />
          <Say id="preview.brand.ppeal" />
        </span>
        <Say id="preview.notice" className="rv-paper-kind" values={{ term: paper.filingTerm.toUpperCase() }} />
      </div>
      <p className="rv-paper-re" data-re-line>{paper.reLine}</p>
      <Bars widths={[1, 0.96, 0.82]} />
      {paper.compValues.length > 0 && (
        <div className="rv-paper-chips">
          {paper.compValues.slice(0, 3).map((v: Money<"market">, i) => (
            <div className="rv-paper-chip" key={i}>
              <Say id="preview.comp" as="small" values={{ n: i + 1 }} />
              <b>{money(v)}</b>
            </div>
          ))}
        </div>
      )}
      <Bars widths={[0.9, 1, 0.7]} />
      <div className="rv-paper-sign">
        {signedCaption === null ? (
          // No signature held: Tyler's approved caption in place of the app's "—" (draft C, `WALK_COPY`).
          <span className="rv-paper-caption" data-held-copy="review.unsignedCaption">{heldText("review.unsignedCaption")}</span>
        ) : (
          <>
            {signatureName.trim() === "" ? (
              <Say id="preview.unsigned" className="rv-paper-name" />
            ) : (
              <span className="rv-paper-name" style={{ fontFamily: SIGNATURE_FONT }}>{signatureName}</span>
            )}
            <span className="rv-paper-caption" data-held-copy="review.signedCaption">{signedCaption}</span>
          </>
        )}
      </div>
      <button
        type="button"
        className="rv-paper-open"
        aria-label={word("preview.a11y")}
        data-held-copy="preview.open"
        disabled={busy}
        onClick={onOpen}
      >
        {word("preview.open")}
        <span aria-hidden="true"> ↗</span>
      </button>
    </div>
  );
}

function Bars({ widths }: { widths: readonly number[] }) {
  return (
    <div className="rv-paper-bars" aria-hidden="true">
      {widths.map((w, i) => <i key={i} style={{ width: `${w * 100}%` }} />)}
    </div>
  );
}

/** The app's `.sheet`: a title bar (cancel on the left, the confirming action on the right where there is one) and its
 *  body. A modal dialog: focus moves in and comes back, and Escape cancels.
 *
 *  `close` (WALK-FIX-1): a sheet with nothing to confirm can carry a ✕ on the right, named by `close.id` for a screen
 *  reader and showing no word, and a click on the dim backdrop around it closes it too. The app's sheets close by a
 *  swipe; a page needs a way out that reads as one. Only for a sheet that holds nothing typed, so a stray click loses
 *  nothing (the preview, today). */
export function ReviewSheet({
  titleId, kind, cancel, confirm, close, children,
}: {
  titleId: ReviewWordingId;
  kind: string;
  cancel: { id: ReviewWordingId; onPress: () => void };
  confirm?: { id: ReviewWordingId; onPress: () => void; disabled?: boolean };
  close?: { id: ReviewWordingId; onPress: () => void };
  children: ReactNode;
}) {
  const headingId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => {
      before?.focus?.();
    };
  }, []);
  return (
    <div
      className="alert-backdrop"
      data-backdrop
      onClick={close === undefined ? undefined : (e) => {
        if (e.target === e.currentTarget) close.onPress();
      }}
    >
      <div
        ref={dialog}
        tabIndex={-1}
        className="app-sheet rv-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-sheet={kind}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            cancel.onPress();
          }
        }}
      >
        <div className="app-sheet-bar">
          <button type="button" className="btn quiet" data-held-copy={cancel.id} onClick={cancel.onPress}>{word(cancel.id)}</button>
          <h2 id={headingId} data-held-copy={titleId}>{word(titleId)}</h2>
          {confirm === undefined && close !== undefined ? (
            <button type="button" className="btn quiet sheet-close" aria-label={word(close.id)} data-held-copy={close.id} onClick={close.onPress}>
              <span aria-hidden="true">✕</span>
            </button>
          ) : confirm === undefined ? <span /> : (
            <button
              type="button"
              className="btn quiet strong"
              data-held-copy={confirm.id}
              disabled={confirm.disabled === true}
              onClick={confirm.onPress}
            >
              {word(confirm.id)}
            </button>
          )}
        </div>
        <div className="app-sheet-body">{children}</div>
      </div>
    </div>
  );
}

// ── "Sign your appeal" (SignatureEditor.swift) ─────────────────────────────────────────────────────────────────────

export function SignatureSheet({
  appealId, context, suggestedName, onClose,
}: { appealId: string; context: string; suggestedName: string; onClose: () => void }) {
  const existing = useSignature(appealId);
  const [mode, setMode] = useState<"draw" | "type">("draw");
  const [typed, setTyped] = useState(suggestedName);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const drawing = useRef<Stroke | null>(null);
  const pad = useRef<HTMLCanvasElement>(null);
  const typedId = useId();
  const canSave = mode === "draw" ? strokes.length > 0 : typed.trim() !== "";

  // The pad shows the owner's strokes in the page's ink; the saved picture is redrawn in black (./signaturePng.ts).
  useEffect(() => {
    const canvas = pad.current;
    const ctx = canvas?.getContext?.("2d") ?? null;
    if (canvas === null || ctx === null) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = getComputedStyle(canvas).color || "#ecece8";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes) {
      const [first, ...rest] = s.points;
      if (first === undefined) continue;
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (const p of rest) ctx.lineTo(p.x, p.y);
      if (rest.length === 0) ctx.lineTo(first.x + 0.1, first.y + 0.1);
      ctx.stroke();
    }
  }, [strokes, mode]);

  const at = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const clear = () => {
    setStrokes([]);
    setTyped("");
    forgetSignature(appealId);
  };

  const save = () => {
    const png = mode === "draw" ? drawnSignaturePng(strokes) : typedSignaturePng(typed);
    if (png === null) return;
    holdSignature(appealId, png);
    onClose();
  };

  return (
    <ReviewSheet titleId="sign.title" kind="signature" cancel={{ id: "feedback.cancel", onPress: onClose }}>
      <div className="rv-sign">
        <Say id="sign.signing" as="p" className="rv-sign-lead" values={{ context }} />
        <Say id="sign.explain" as="p" className="muted" />
        <div className="rv-segmented" role="group" aria-label={word("sign.style")} data-held-copy="sign.style">
          {(["draw", "type"] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              className={mode === m ? "on" : undefined}
              data-held-copy={m === "draw" ? "sign.draw" : "sign.type"}
              onClick={() => setMode(m)}
            >
              {word(m === "draw" ? "sign.draw" : "sign.type")}
            </button>
          ))}
        </div>
        {mode === "draw" ? (
          <div className="rv-sign-pad">
            <canvas
              ref={pad}
              aria-label={word("sign.here")}
              data-signature-pad
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture?.(e.pointerId);
                drawing.current = { points: [at(e)] };
                setStrokes((s) => [...s, drawing.current as Stroke]);
              }}
              onPointerMove={(e) => {
                const stroke = drawing.current;
                if (stroke === null) return;
                const next = { points: [...stroke.points, at(e)] };
                drawing.current = next;
                setStrokes((s) => [...s.slice(0, -1), next]);
              }}
              onPointerUp={() => {
                drawing.current = null;
              }}
              onPointerCancel={() => {
                drawing.current = null;
              }}
            />
            <span className="rv-sign-line" aria-hidden="true" />
            {strokes.length === 0 && <Say id="sign.here" className="rv-sign-hint" />}
          </div>
        ) : (
          <div className="rv-sign-typed">
            <label className="field" htmlFor={typedId}>
              <span className="visually-hidden" data-held-copy="sign.typed.placeholder">{word("sign.typed.placeholder")}</span>
              <input
                id={typedId}
                type="text"
                autoCapitalize="words"
                autoComplete="name"
                spellCheck={false}
                placeholder={word("sign.typed.placeholder")}
                value={typed}
                onChange={(e) => setTyped(e.currentTarget.value)}
              />
            </label>
            <div className="rv-sign-preview" style={{ fontFamily: SIGNATURE_FONT }} data-typed-preview>
              {typed.trim() === "" ? <Say id="sign.typed.preview" className="rv-sign-ghost" /> : typed}
            </div>
            <Say id="sign.typed.footer" as="p" className="faint" />
          </div>
        )}
        <div className="rv-sign-actions">
          <button type="button" className="btn ghost danger" data-held-copy="sign.clear" onClick={clear}
            disabled={strokes.length === 0 && existing === null && typed.trim() === ""}>
            {word("sign.clear")}
          </button>
          <button type="button" className="btn primary" disabled={!canSave} onClick={save}
            data-held-copy={existing === null ? "sign.save" : "sign.update"}>
            {word(existing === null ? "sign.save" : "sign.update")}
          </button>
        </div>
        {existing !== null && <Say id="sign.signed" as="p" className="rv-sign-done" />}
      </div>
    </ReviewSheet>
  );
}

// ── "I need a change to this letter": one door, two paths (WALK-FIX-4) ──────────────────────────────────────────────

/**
 * THE ONE WAY TO CHANGE THIS LETTER, AND ITS TWO PATHS (WALK-FIX-4, R243). Tyler, 2026-10-06, on Review:
 * "The little icon about revise your details isnt clear.. also I feel like that section should fall under the I need a
 * change to this letter section" and "I like theidea of turning the I need a change to this letter being a single button
 * that leads you on two paths.. and lets make it clear its there".
 *
 * The button under the letter paper opens this sheet, and the sheet holds the two ways the app changes a letter:
 *   (a) "Request a change" — the AI change request (the app's `LetterFeedbackSheet`), with the app's own line for it;
 *   (b) "Revise from your details" — step back through the steps (the app's `reviseSection`, ReviewView.swift:1402-1425),
 *       with its own line, its "Nothing is re-charged…" footer, and a clear picture: a pencil with a back arrow.
 *
 * Path (b) is ALWAYS here: the app offers it "deliberately ungated", even on an appeal whose generations (or edits) are
 * spent, because it is still the way to correct a sale or a value (ReviewView.swift:1395-1401, over `reviseSection`).
 * Path (a) is here only where the app shows its button (a letter with words in it); while a change is being made it is
 * the app's "Making your changes…", never a second request (WEB-RESUME). Every word is the app's; the sheet's title is
 * the button's own. The ways out are the app's sheet's: "Cancel" and Escape, and focus goes back to the button.
 */
export function ChangeSheet({
  request, onRevise, onClose,
}: {
  /** Path (a): null where there is no letter text to change, "working" while a change is being made, else its press. */
  request: null | "working" | (() => void);
  onRevise: () => void;
  onClose: () => void;
}) {
  return (
    <ReviewSheet titleId="review.change.title" kind="change" cancel={{ id: "feedback.cancel", onPress: onClose }}>
      {request !== null && (
        <ReviewSection data-change-path="request">
          {request === "working" ? (
            <p className="rv-working" role="status" data-held-copy="review.change.working">
              <span className="dot" aria-hidden="true" /> {word("review.change.working")}
            </p>
          ) : (
            <button type="button" className="rv-row" onClick={request}>
              <span className="rv-row-tile" aria-hidden="true"><RequestChangeIcon /></span>
              <span className="rv-row-text">
                <Say id="feedback.title" as="b" />
                <Say id="review.change.subtitle" as="small" />
              </span>
              <span className="rv-row-chevron" aria-hidden="true">›</span>
            </button>
          )}
        </ReviewSection>
      )}
      <ReviewSection data-change-path="revise" footer={<Say id="review.revise.footer" />}>
        <button type="button" className="rv-row" onClick={onRevise}>
          <span className="rv-row-tile" aria-hidden="true"><ReviseIcon /></span>
          <span className="rv-row-text">
            <Say id="review.revise.title" as="b" />
            <Say id="review.revise.subtitle" as="small" />
          </span>
          <span className="rv-row-chevron" aria-hidden="true">›</span>
        </button>
      </ReviewSection>
    </ReviewSheet>
  );
}

/** The button's picture: SF Symbols' `square.and.pencil`, the app's own for this button (ReviewView.swift:907). */
export function ChangeIcon() {
  return (
    <svg className="rv-icon" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="square.and.pencil">
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        d="M11 4.5H6.5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V13" />
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        d="M17.6 3.9a1.9 1.9 0 0 1 2.7 2.7l-7.8 7.8-3.6.9.9-3.6 7.8-7.8Z" />
    </svg>
  );
}

/** Path (a)'s picture: lines of writing and a pencil — a change to the words. Drawing only. */
function RequestChangeIcon() {
  return (
    <svg className="rv-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="text.pencil">
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" d="M3.5 6h10M3.5 10.5h7M3.5 15h4" />
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        d="M17.9 8.6a1.8 1.8 0 0 1 2.5 2.5l-7.2 7.2-3.3.8.8-3.3 7.2-7.2Z" />
    </svg>
  );
}

/** Path (b)'s picture: a pencil with a back arrow — go back and change it (Tyler: the old "↶" glyph "isnt clear"). */
function ReviseIcon() {
  return (
    <svg className="rv-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="pencil.back">
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        d="M7.5 3 3.5 7l4 4M3.5 7h7.5a4.5 4.5 0 0 1 4.3 3.2" />
      <path fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        d="M18.2 11.9a1.8 1.8 0 0 1 2.5 2.5l-6.4 6.4-3.3.8.8-3.3 6.4-6.4Z" />
    </svg>
  );
}

// ── "Request a change" (LetterFeedbackSheet.swift) ─────────────────────────────────────────────────────────────────

const STARTERS: readonly { label: ReviewWordingId; seed: ReviewWordingId | null }[] = [
  { label: "feedback.starter.missing", seed: "feedback.starter.missing.seed" },
  { label: "feedback.starter.wrong", seed: "feedback.starter.wrong.seed" },
  { label: "feedback.starter.weak", seed: "feedback.starter.weak.seed" },
  { label: "feedback.starter.unclear", seed: "feedback.starter.unclear.seed" },
  { label: "feedback.starter.else", seed: null },
];

export function FeedbackSheet({
  letterText, remaining, onCancel, onSend,
}: { letterText: string; remaining: number; onCancel: () => void; onSend: (concern: string) => void }) {
  const [text, setText] = useState("");
  const [showLetter, setShowLetter] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const boxId = useId();
  const trimmed = text.trim();
  // The app's own threshold for Send (`tooShort`, LetterFeedbackSheet.swift): fifteen characters.
  const tooShort = trimmed.length < 15;
  return (
    <ReviewSheet
      titleId="feedback.title"
      kind="feedback"
      cancel={{ id: "feedback.cancel", onPress: onCancel }}
      confirm={{ id: "feedback.send", onPress: () => onSend(trimmed), disabled: tooShort }}
    >
      <ReviewSection header={<Say id="feedback.starters.header" />} footer={<Say id="feedback.starters.footer" />}>
        {STARTERS.map((s) => (
          <button
            key={s.label}
            type="button"
            className="rv-row rv-row-plain"
            data-held-copy={s.label}
            onClick={() => {
              if (trimmed === "" && s.seed !== null) setText(word(s.seed));
              box.current?.focus();
            }}
          >
            <span className="rv-row-text"><b>{word(s.label)}</b></span>
            <span className="rv-row-chevron" aria-hidden="true">+</span>
          </button>
        ))}
      </ReviewSection>
      {letterText.trim() !== "" && (
        <ReviewSection header={<Say id="feedback.letter.header" />} footer={<Say id="feedback.letter.footer" />}>
          <button type="button" className="rv-row rv-row-plain" aria-expanded={showLetter} onClick={() => setShowLetter((v) => !v)}
            data-held-copy={showLetter ? "review.letter.hide" : "review.letter.read"}>
            <span className="rv-row-text"><b>{word(showLetter ? "review.letter.hide" : "review.letter.read")}</b></span>
          </button>
          {showLetter && <div className="rv-letter-full">{letterText}</div>}
        </ReviewSection>
      )}
      <ReviewSection
        header={<label htmlFor={boxId} data-held-copy="feedback.words.header">{word("feedback.words.header")}</label>}
        footer={<Say id="feedback.words.footer" values={{ remaining, plural: remaining === 1 ? "" : "s" }} className="rv-pre" />}
      >
        <textarea
          id={boxId}
          ref={box}
          className="rv-textarea"
          rows={6}
          maxLength={4000}
          placeholder={word("feedback.words.placeholder")}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      </ReviewSection>
    </ReviewSheet>
  );
}

// ── "Your appeal letter" (LetterPreviewSheet.swift) and "Email me a copy" (EmailCopyRow.swift) ────────────────────

export function PdfSheet({
  pdf, onDownload, onClose, email,
}: {
  pdf: { base64: string; filename: string };
  onDownload: () => void;
  onClose: () => void;
  /** Null when the server does not offer "Email me a copy". */
  email: { prefill: string; send: (to: string) => Promise<V2Result<EmailLetterResponse>> } | null;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== "function") return;
    const binary = atob(pdf.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const made = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [pdf.base64]);
  return (
    <ReviewSheet titleId="pdf.title" kind="pdf" cancel={{ id: "pdf.edit", onPress: onClose }} close={{ id: "pdf.close", onPress: onClose }}>
      <div className="rv-pdf" data-pdf-filename={pdf.filename}>
        {url !== null && <iframe className="rv-pdf-frame" src={url} title={word("pdf.title")} />}
      </div>
      <div className="rv-pdf-actions">
        <button type="button" className="btn primary block" data-held-copy="pdf.download" onClick={onDownload}>
          {word("pdf.download")}
        </button>
        {email !== null && <EmailCopyRow prefill={email.prefill} send={email.send} />}
      </div>
    </ReviewSheet>
  );
}

/** A refusal in one line, under the control it answers: the server's sentence, or the transport's. */
export function FailureLine({ failure }: { failure: V2Failure }) {
  if (failure.kind === "v2Error") return <CopyLine copy={failure.error.error.copy} />;
  return <HeldLine id="transport.unreachable" />;
}

type EmailPhase = { kind: "collapsed" } | { kind: "editing" } | { kind: "sending" } | { kind: "sent"; to: string }
  | { kind: "failed"; failure: V2Failure };

export function EmailCopyRow({ prefill, send }: { prefill: string; send: (to: string) => Promise<V2Result<EmailLetterResponse>> }) {
  const [phase, setPhase] = useState<EmailPhase>({ kind: "collapsed" });
  const [address, setAddress] = useState("");
  const fieldId = useId();
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (phase.kind === "editing") field.current?.focus();
  }, [phase.kind]);
  const submit = async () => {
    if (address.trim() === "") return;
    setPhase({ kind: "sending" });
    const result = await send(address);
    setPhase(result.ok ? { kind: "sent", to: result.response.body.sent.to } : { kind: "failed", failure: result });
  };
  if (phase.kind === "collapsed") {
    return (
      <button type="button" className="rv-email-open" data-held-copy="email.open"
        onClick={() => { setAddress(prefill); setPhase({ kind: "editing" }); }}>
        ✉ {word("email.open")}
      </button>
    );
  }
  if (phase.kind === "sent") return <Say id="email.sent" as="p" className="rv-email-sent" values={{ to: phase.to }} />;
  if (phase.kind === "failed") {
    return (
      <div className="rv-email-failed" role="alert">
        <FailureLine failure={phase.failure} />
        <button type="button" className="btn quiet" data-held-copy="email.tryAgain" onClick={() => setPhase({ kind: "editing" })}>
          {word("email.tryAgain")}
        </button>
      </div>
    );
  }
  return (
    <form className="rv-email" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <div className="rv-email-row">
        <label htmlFor={fieldId} className="visually-hidden" data-held-copy="email.open">{word("email.open")}</label>
        <input
          id={fieldId}
          ref={field}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={word("email.placeholder")}
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <button type="submit" className="btn primary" data-held-copy="email.send"
          disabled={phase.kind === "sending" || address.trim() === ""}>
          {word("email.send")}
        </button>
      </div>
      <p className="faint" role={phase.kind === "sending" ? "status" : undefined}
        data-held-copy={phase.kind === "sending" ? "email.sending" : "email.promise"}>
        {word(phase.kind === "sending" ? "email.sending" : "email.promise")}
      </p>
    </form>
  );
}
